import React, { useState, useMemo, useCallback, useRef, useEffect, useContext } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { AutoComplete } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Calendar } from 'primereact/calendar';
import { usePracticeAreasQuery } from 'hooks/queries/usePracticeAreasQuery';
import { useUsersQuery } from 'hooks/queries/useUsersQuery';
import { timeEntriesStatusOptions, timeEntriesBillingOptions } from '../../constants/timeEntriesOptions';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import GlobalTooltip from 'components/GlobalTooltip';
import debounce from 'lodash.debounce';
import { UserDetailsContext } from 'context/userDetailsContext';
import { listCasesAndLeads } from 'services/CaseServices';
import { LoaderComponent } from 'shared/LoaderComponent';
import { generateFullName } from 'utils/generateFullNameUtils';
import { sortByKey } from 'utils/utils';
import { formatTimeEntriesMultiSelectContactList } from '../../utils/timeEntriesUserFormatter';
import useFilterBreakpoint from 'hooks/useFilterBreakpoint';

const TimeEntriesFiltersV2 = ({
  searchKey,
  onChangeSearch,
  onSearchKeyDown,
  selectedDate,
  onChangeCalendar,
  selectedStatus,
  onChangeStatus,
  selectedCaseId,
  onChangeCase,
  onClearCase,
  selectedPractice,
  onChangePractice,
  selectedBillingStatus,
  onChangeBillingStatus,
  selectedUser,
  onChangeUser,
  showUserFilter,
  onShowUserFilter,
  refreshData,
  is_automated,
  caseDetailsPage,
  isFromCaseSpecificModule,
  isLeadSpecific,
}) => {
  const userContext = useContext(UserDetailsContext);
  const [caseFullList, setCaseFullList] = useState([]);
  const [searchInputValue, setSearchInputValue] = useState(''); // Track what user is typing
  const [load, setLoad] = useState(false);
  const [localSearchKey, setLocalSearchKey] = useState(searchKey || '');
  const ref = useRef(null);
  const debouncedSearchRef = useRef(null);
  const debouncedCaseSearchRef = useRef(null);

  // Use React Query hook for practice areas
  const { practiceAreaList, isPracticeAreaLoading } = usePracticeAreasQuery({
    loadFullList: true,
    status: 'active',
    excludeFromLoading: false,
    sort_order: 'alphabet_ascending',
    pagination_token: '',
    pagination_direction: 'forward',
  });

  // Use React Query hook for users (only when showUserFilter is true)
  const { userList, isUserLoading } = useUsersQuery({
    status: 'ACTIVE',
    is_include_groups: true,
    active_groups: true,
    excludeFromLoading: false,
    is_confirmed_user: true,
    is_detailed: false,
    enabled: showUserFilter,
  });

  // Map practice areas to dropdown options
  const practiceAreaOptions = useMemo(() => {
    if (practiceAreaList && practiceAreaList.length > 0) {
      return practiceAreaList.map((val) => ({
        label: val.practice_area_name,
        value: val.practice_area_id,
      }));
    }
    return [];
  }, [practiceAreaList]);

  // Format firm user list for dropdown (with groups) - uses firm_user_id as assignee_id
  const firmUserList = useMemo(() => {
    if (!userList || !Array.isArray(userList) || userList.length === 0) return [];

    // Format the list using TimeEntries-specific formatter (uses firm_user_id)
    const formattedList = formatTimeEntriesMultiSelectContactList(userList);
    const sortedList = formattedList?.map((item) => {
      if (item?.code === 'Users') {
        return { ...item, items: sortByKey(item.items, 'first_name') };
      } else if (item?.code === 'Groups') {
        return {
          ...item,
          items: item.items.sort((a, b) => (a.group_name?.toLowerCase() || '').localeCompare(b.group_name?.toLowerCase() || '')),
        };
      }
      return item;
    });

    return sortedList || [];
  }, [userList]);

  const caseAutocompleteItemTemplate = useCallback((item) => {
    if (item?.__empty) {
      return (
        <span className="text-muted px-2 py-2 d-block small" style={{ pointerEvents: 'none' }}>
          {item.label}
        </span>
      );
    }
    return item?.label ?? '';
  }, []);

  // Fetch case/lead list
  const getAllCaseList = useCallback(
    (keyword = '', limit = 15) => {
      // For automated time entries, only show cases (is_case_list = true), otherwise show both (is_case_list = undefined)
      // When is_automated is true, is_case_list should be true (only cases)
      // When is_automated is false/undefined, is_case_list should be undefined (includes leads)
      const is_case_list = is_automated ? true : undefined; // If is_automated is truthy, is_case_list is true. If falsy, is_case_list is undefined.
      listCasesAndLeads(true, '', false, keyword, limit, true, [], true, false, is_case_list)
        .then((res) => {
          let tempArray = [];
          res?.data?.case_list?.forEach((item) => {
            tempArray.push({
              label:
                item && item.case_description && item.case_description.case_name
                  ? `${item.unique_number ? `${item.unique_number} ` : ''}${item.case_description.case_name}`
                  : 'TBD',
              value: item.case_description.case_name,
              id: item?.case_id,
            });
          });
          if (!caseDetailsPage && !isLeadSpecific && !is_automated) {
            res?.data?.lead_list?.forEach((item) => {
              tempArray.push({
                label:
                  item && item.case_description && item.case_description.case_name
                    ? `${item.unique_number ? `${item.unique_number} ` : ''}${item.case_description.case_name}`
                    : 'TBD',
                value: item.case_description.case_name,
                id: item?.case_id,
                is_lead: item?.is_lead,
              });
            });
          }
          if (tempArray.length === 0) {
            tempArray.push({
              __empty: true,
              label: is_automated ? 'No cases found' : 'No cases or leads found',
              value: '',
              id: null,
            });
          }
          setCaseFullList(tempArray);
        })
        .catch((err) => {
          console.log(err);
        });
    },
    [caseDetailsPage, isLeadSpecific, is_automated]
  );

  // Initial load of case list
  useEffect(() => {
    getAllCaseList();
  }, [getAllCaseList]);

  // Create debounced search function that uses current state
  useEffect(() => {
    // Create debounced function that calls parent's onChangeSearch
    debouncedSearchRef.current = debounce((value) => {
      if (onChangeSearch) {
        // Create a synthetic event object to match the expected format
        const syntheticEvent = {
          target: { value },
        };
        onChangeSearch(syntheticEvent);
      }
    }, 500); // 500ms delay

    // Cleanup on unmount
    return () => {
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }
    };
  }, [onChangeSearch]);

  // Sync local search key with prop
  useEffect(() => {
    setLocalSearchKey(searchKey || '');
  }, [searchKey]);

  // Sync selectedCaseId from parent - clear searchInputValue when case is selected externally
  useEffect(() => {
    if (selectedCaseId?.id) {
      setSearchInputValue('');
    }
  }, [selectedCaseId]);

  // Create debounced case search function using ref (more reliable pattern, matches search key debounce)
  // Only debounce the API call, not the searchValue update
  useEffect(() => {
    debouncedCaseSearchRef.current = debounce((query) => {
      getAllCaseList(query, 15);
      setLoad(false);
    }, 500); // 500ms delay

    // Cleanup on unmount
    return () => {
      if (debouncedCaseSearchRef.current) {
        debouncedCaseSearchRef.current.cancel();
      }
    };
  }, [getAllCaseList]);

  const handleSearchCases = (e) => {
    const query = e?.query || '';
    // Update search input value immediately for responsive UI
    setSearchInputValue(query);
    // Clear selected case when user starts typing (if query doesn't match selected case)
    if (selectedCaseId && query !== selectedCaseId?.value) {
      onClearCase && onClearCase();
    }
    // Debounce only the API call
    if (debouncedCaseSearchRef.current) {
      debouncedCaseSearchRef.current(query);
    }
  };

  const clickHandler = () => {
    ref.current?.search('', '', 'dropdown');
    setLoad(true);
  };

  const handleClear = () => {
    if (onClearCase) {
      onClearCase();
    }
    setSearchInputValue('');
  };

  const assignToItemTemplate = (option) => {
    return (
      <>
        {option?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
        {generateFullName(option)}
      </>
    );
  };
  const { showFilters, setShowFilters } = useFilterBreakpoint();

  return (
    <div className="d-flex flex-wrap align-items-center filter-wrap mb-3 p-lg-0 p-2 w-100 align-items-center justify-content-between time-entries-filter">
      <div className="p-2 search-wraper">
        <span className="p-input-icon-left input-shadow">
          <i className="icon-search F-size16"></i>
          <TextSnippetWrapper
            type="text"
            placeholder="Search"
            onChange={(e) => {
              const value = e.target.value;
              setLocalSearchKey(value);
              // Debounce the API call - will trigger after user stops typing for 500ms
              debouncedSearchRef.current(value);
            }}
            value={localSearchKey}
            onKeyDown={(e) => {
              const code = e.keyCode ? e.keyCode : e.which;
              if (code === 13) {
                // Enter key pressed - cancel debounce and trigger immediately
                if (debouncedSearchRef.current) {
                  debouncedSearchRef.current.cancel();
                }
                if (onChangeSearch) {
                  onChangeSearch(e);
                }
              }
              // Also call parent's onSearchKeyDown if provided
              if (onSearchKeyDown) {
                onSearchKeyDown(e);
              }
            }}
            className="p-inputtext p-component input-search"
            autoComplete="off"
          />
        </span>
      </div>
      <div className="d-block d-lg-none w-100">
        <Button
          className="p-button p-component btn p-button-secondary outline w-100 filter-toggle-btn"
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          aria-expanded={showFilters}
          aria-controls="timeentries-filter-section"
          label="Filters"
        >
          <span
            style={{
              fontSize: 18,
              transition: 'transform 0.2s',
              display: 'inline-block',
              transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <i className="fa fa-solid fa-angle-down"></i>
          </span>
        </Button>
      </div>
      <div
        className={`d-flex flex-wrap align-items-center p-2 gap-2 filter-toggle-section filter-section${showFilters ? ' show' : ''}`}
        id="timeentries-filter-section"
        aria-hidden={!showFilters}
      >
        <label className="text-medium">Filters :</label>
        <div className="d-flex flex-column r-pb-0 filter-wraper">
          <Calendar
            className="input-shadow filter-calender"
            id="icon"
            selectionMode="range"
            value={selectedDate}
            onChange={onChangeCalendar}
            showIcon
            placeholder="Date"
          />
        </div>
        <div className="d-flex flex-column filter-wraper">
          <Dropdown
            options={timeEntriesStatusOptions}
            onChange={onChangeStatus}
            value={selectedStatus}
            placeholder="Invoice Status"
            className="searchable-dropdown"
            showClear
          />
        </div>
        {!caseDetailsPage && (
          <div className="d-flex flex-column filter-wraper">
            <GlobalTooltip
              tooltip={selectedCaseId?.id && selectedCaseId?.label ? selectedCaseId.label : null}
              position="top"
              className="w-100 d-block"
              showDelay={300}
            >
              <AutoComplete
                ref={ref}
                value={searchInputValue || (selectedCaseId?.id ? selectedCaseId : null)}
                suggestions={caseFullList}
                completeMethod={handleSearchCases}
                itemTemplate={caseAutocompleteItemTemplate}
                onChange={(e) => {
                  if (e?.value && typeof e?.value === 'object' && e?.value?.__empty) {
                    return;
                  }
                  // Only update parent when user SELECTS an item from dropdown (object with id)
                  if (e?.value && typeof e?.value === 'object' && e?.value?.id) {
                    // User selected a case from dropdown
                    setSearchInputValue('');
                    onChangeCase && onChangeCase(e);
                  } else if (e?.value === '' || e?.value === null || e?.value === undefined) {
                    // User cleared the field
                    setSearchInputValue('');
                    if (onClearCase) {
                      onClearCase();
                    }
                  } else {
                    // User is typing - update local state only, don't call parent
                    // This prevents flickering by not updating selectedCaseId while typing
                    setSearchInputValue(e?.value || '');
                    // If there was a selected case and user is now typing, clear it
                    if (selectedCaseId?.id) {
                      onClearCase && onClearCase();
                    }
                  }
                }}
                onClick={clickHandler}
                placeholder={is_automated ? 'Case Name' : 'Case/Lead Name'}
                field="label"
                className="mx-1"
                type="search"
                dropdown
                onDropdownClick={() => setLoad(true)}
                onClear={handleClear}
                filter
              />
            </GlobalTooltip>
          </div>
        )}

        {!isFromCaseSpecificModule && !isLeadSpecific && (
          <div className="d-flex flex-column filter-wraper r-pb-0">
            <MultiSelect
              value={selectedPractice}
              options={practiceAreaOptions}
              onChange={onChangePractice}
              placeholder="Practice Area"
              maxSelectedLabels={1}
              display="chip"
              filter
              className="input-shadow w-100 F-size14"
              panelFooterTemplate={isPracticeAreaLoading ? LoaderComponent : null}
            />
          </div>
        )}

        <div className="d-flex flex-column filter-wraper">
          <Dropdown
            options={timeEntriesBillingOptions}
            onChange={onChangeBillingStatus}
            value={selectedBillingStatus}
            placeholder="Billing Status"
            showClear
          />
        </div>
        <div className="d-flex flex-column filter-wraper r-pb-0">
          {!showUserFilter ? (
            <Button
              type="button"
              label="Show User"
              className="p-button me-2 button-text px-1 adv-filter text-bold text-primary-main"
              onClick={onShowUserFilter}
            />
          ) : (
            <Dropdown
              placeholder="Firm User"
              options={firmUserList}
              optionValue={'assignee_id'}
              optionLabel="assignee_name"
              optionGroupLabel="assignee_name"
              optionGroupChildren="items"
              onChange={onChangeUser}
              value={selectedUser}
              filter
              className="searchable-dropdown"
              itemTemplate={assignToItemTemplate}
              panelFooterTemplate={isUserLoading ? LoaderComponent : null}
            />
          )}
        </div>

        <div className="d-flex flex-column mx-2">
          <div className="pointer ms-0">
            <i className="fas fa-sync-alt" onClick={refreshData}></i>
            <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeEntriesFiltersV2;
